import { HeatTier, HeatMeter, HeatMeterUpdate, HeatStreakEvent, HEAT_TIER_THRESHOLDS } from '@bracketblitz/shared-types';

/**
 * Calculate heat tier based on current streak
 * @param streak Current correct pick streak
 * @returns Heat tier
 */
export function calculateHeatTier(streak: number): HeatTier {
  if (streak >= HEAT_TIER_THRESHOLDS.blazing.minStreak) {
    return 'blazing';
  }
  if (streak >= HEAT_TIER_THRESHOLDS.on_fire.minStreak) {
    return 'on_fire';
  }
  if (streak >= HEAT_TIER_THRESHOLDS.hot.minStreak) {
    return 'hot';
  }
  if (streak >= HEAT_TIER_THRESHOLDS.warm.minStreak) {
    return 'warm';
  }
  return 'cold';
}

/**
 * Get heat multiplier for a given tier
 * @param tier Heat tier
 * @returns Multiplier value
 */
export function getHeatMultiplier(tier: HeatTier): number {
  return HEAT_TIER_THRESHOLDS[tier].multiplier;
}

/**
 * Create a new heat meter for a user
 * @param userId User ID
 * @returns New heat meter instance
 */
export function createHeatMeter(userId: string): HeatMeter {
  return {
    userId,
    currentStreak: 0,
    currentTier: 'cold',
    multiplier: 1.0,
    progress: 0,
    lastUpdateTime: new Date().toISOString(),
    streakHistory: [],
  };
}

/**
 * Update heat meter based on pick result
 * @param heatMeter Current heat meter
 * @param isCorrect Whether the pick was correct
 * @param gameId ID of the game (optional)
 * @returns Updated heat meter and update details
 */
export function updateHeatMeter(
  heatMeter: HeatMeter,
  isCorrect: boolean,
  gameId?: string
): { meter: HeatMeter, update: HeatMeterUpdate } {
  const previousTier = heatMeter.currentTier;
  const previousStreak = heatMeter.currentStreak;
  
  let newStreak: number;
  let newTier: HeatTier;
  
  if (isCorrect) {
    newStreak = previousStreak + 1;
  } else {
    newStreak = 0; // Reset streak on incorrect pick
  }
  
  newTier = calculateHeatTier(newStreak);
  const tierChanged = newTier !== previousTier;
  
  // Calculate progress to next tier
  const progress = getProgressToNextTier(newStreak);
  
  // Create streak event
  const streakEvent: HeatStreakEvent = {
    timestamp: new Date().toISOString(),
    streakLength: newStreak,
    tier: newTier,
    trigger: isCorrect ? 'correct_pick' : 'incorrect_pick',
    gameId,
  };
  
  // Add tier change events if applicable
  const tierEvents: HeatStreakEvent[] = [];
  if (tierChanged) {
    if (newTier > previousTier) {
      tierEvents.push({
        ...streakEvent,
        trigger: 'tier_up',
      });
    } else {
      tierEvents.push({
        ...streakEvent,
        trigger: 'tier_down',
      });
    }
  }
  
  // Reset event if streak was broken
  if (!isCorrect && previousStreak > 0) {
    tierEvents.push({
      ...streakEvent,
      trigger: 'reset',
    });
  }
  
  const updatedMeter: HeatMeter = {
    ...heatMeter,
    currentStreak: newStreak,
    currentTier: newTier,
    multiplier: getHeatMultiplier(newTier),
    progress,
    lastUpdateTime: new Date().toISOString(),
    streakHistory: [
      ...heatMeter.streakHistory,
      streakEvent,
      ...tierEvents,
    ].slice(-50), // Keep last 50 events
  };
  
  const update: HeatMeterUpdate = {
    previousTier,
    newTier,
    previousStreak,
    newStreak,
    tierChanged,
  };
  
  // Add animation if tier changed or special milestone
  if (tierChanged || shouldTriggerOnFire(newStreak, newTier)) {
    update.animation = createHeatAnimation(previousTier, newTier, newStreak);
  }
  
  return { meter: updatedMeter, update };
}

/**
 * Calculate progress to next heat tier
 * @param currentStreak Current streak length
 * @returns Progress as decimal (0-1)
 */
export function getProgressToNextTier(currentStreak: number): number {
  const currentTier = calculateHeatTier(currentStreak);
  
  // Find next tier threshold
  const tiers: HeatTier[] = ['cold', 'warm', 'hot', 'on_fire', 'blazing'];
  const currentIndex = tiers.indexOf(currentTier);
  
  if (currentIndex >= tiers.length - 1) {
    return 1.0; // Already at max tier
  }
  
  const nextTier = tiers[currentIndex + 1];
  const nextThreshold = HEAT_TIER_THRESHOLDS[nextTier].minStreak;
  const currentThreshold = HEAT_TIER_THRESHOLDS[currentTier].minStreak;
  
  const progress = (currentStreak - currentThreshold) / (nextThreshold - currentThreshold);
  return Math.min(Math.max(progress, 0), 1);
}

/**
 * Check if "on fire" animation should trigger
 * @param streak Current streak
 * @param tier Current tier
 * @returns Whether to trigger special animation
 */
export function shouldTriggerOnFire(streak: number, _tier: HeatTier): boolean {
  // Trigger on fire animation when reaching on_fire tier or blazing tier
  return (
    streak === HEAT_TIER_THRESHOLDS.on_fire.minStreak ||
    streak === HEAT_TIER_THRESHOLDS.blazing.minStreak
  );
}

/**
 * Create animation config for heat meter updates
 * @param previousTier Previous heat tier
 * @param newTier New heat tier
 * @param streak Current streak
 * @returns Animation configuration
 */
function createHeatAnimation(
  previousTier: HeatTier,
  newTier: HeatTier,
  _streak: number
) {
  const tierOrder: HeatTier[] = ['cold', 'warm', 'hot', 'on_fire', 'blazing'];
  const previousIndex = tierOrder.indexOf(previousTier);
  const newIndex = tierOrder.indexOf(newTier);
  
  if (newIndex > previousIndex) {
    // Tier increased
    switch (newTier) {
      case 'blazing':
        return {
          type: 'blazing' as const,
          duration: 3000,
          effects: ['screen_flash', 'fire_particles', 'intense_glow'],
        };
      case 'on_fire':
        return {
          type: 'on_fire' as const,
          duration: 2500,
          effects: ['flame_burst', 'heat_glow', 'streak_flame'],
        };
      case 'hot':
        return {
          type: 'tier_up' as const,
          duration: 2000,
          effects: ['warm_glow', 'ember_particles'],
        };
      case 'warm':
        return {
          type: 'tier_up' as const,
          duration: 1500,
          effects: ['subtle_glow'],
        };
      default:
        return {
          type: 'tier_up' as const,
          duration: 1000,
          effects: ['pulse'],
        };
    }
  } else if (newIndex < previousIndex) {
    // Tier decreased (streak broken)
    return {
      type: 'streak_broken' as const,
      duration: 2000,
      effects: ['fade_out', 'cool_down'],
    };
  }
  
  return undefined;
}

/**
 * Get heat tier color for UI
 * @param tier Heat tier
 * @returns Hex color code
 */
export function getHeatTierColor(tier: HeatTier): string {
  const colors: Record<HeatTier, string> = {
    cold: '#94a3b8',      // slate-400
    warm: '#f97316',      // orange-500
    hot: '#ef4444',       // red-500
    on_fire: '#dc2626',   // red-600
    blazing: '#7c3aed',   // violet-600
  };
  
  return colors[tier];
}

/**
 * Get heat tier emoji for display
 * @param tier Heat tier
 * @returns Emoji representing the tier
 */
export function getHeatTierEmoji(tier: HeatTier): string {
  const emojis: Record<HeatTier, string> = {
    cold: '🧊',
    warm: '🔥',
    hot: '🔥🔥',
    on_fire: '🔥🔥🔥',
    blazing: '🔥🔥🔥🔥',
  };
  
  return emojis[tier];
}

/**
 * Calculate heat bonus points for a correct pick
 * @param tier Current heat tier
 * @param basePoints Base points for the pick
 * @returns Additional bonus points from heat
 */
export function calculateHeatBonus(tier: HeatTier, basePoints: number): number {
  const multiplier = getHeatMultiplier(tier);
  return Math.floor(basePoints * (multiplier - 1.0));
}